import type { BotLevel, TrumpMode } from '@mendikot/engine';
import { detectLang, isLang, type Lang } from '@mendikot/i18n';
import { isThemeId, type ThemeId } from '@mendikot/protocol';
import { createStore, type StoreApi } from 'zustand/vanilla';
import type { KeyValueStore } from './services';

export interface Settings {
  name: string;
  avatarId: string;
  lang: Lang;
  sound: boolean;
  haptics: boolean;
  reducedMotion: 'system' | 'on' | 'off';
  fourColor: boolean;
  analyticsConsent: 'unknown' | 'granted' | 'denied';
  /** When the player confirmed they are 18+ (DPDP: the app is not for children). */
  ageConfirmedAt: number | null;
  /** Version of the privacy notice the player accepted; a newer notice is shown again. */
  noticeVersion: string | null;
  botLevel: BotLevel;
  practiceTrump: TrumpMode;
  /** User-picked theme; null follows Remote Config (the festival calendar). */
  themeOverride: ThemeId | null;
  pushPromptedAt: number | null;
  matchesCompleted: number;
}

const KEY = 'mendikot.settings.v1';

export const AVATARS = [
  'peacock',
  'lotus',
  'elephant',
  'tiger',
  'mango',
  'kite',
  'diya',
  'tabla',
] as const;

export function defaultSettings(languages: readonly string[] = []): Settings {
  return {
    name: '',
    avatarId: AVATARS[Math.floor(Math.random() * AVATARS.length)]!,
    lang: detectLang(languages),
    sound: true,
    haptics: true,
    reducedMotion: 'system',
    fourColor: false,
    analyticsConsent: 'unknown',
    ageConfirmedAt: null,
    noticeVersion: null,
    botLevel: 'normal',
    practiceTrump: 'CUT',
    themeOverride: null,
    pushPromptedAt: null,
    matchesCompleted: 0,
  };
}

function sanitize(raw: Partial<Settings>, fallback: Settings): Settings {
  const merged = { ...fallback, ...raw };
  if (!isLang(merged.lang)) merged.lang = fallback.lang;
  if (merged.themeOverride !== null && !isThemeId(merged.themeOverride))
    merged.themeOverride = null;
  if (typeof merged.ageConfirmedAt !== 'number') merged.ageConfirmedAt = null;
  if (typeof merged.noticeVersion !== 'string') merged.noticeVersion = null;
  if (typeof merged.name !== 'string') merged.name = '';
  merged.name = merged.name.slice(0, 20);
  return merged;
}

export type SettingsStore = StoreApi<Settings> & { update(patch: Partial<Settings>): void };

/** Persistent per-device preferences (plain key/value storage on web and native). */
export function createSettingsStore(
  kv: KeyValueStore,
  languages: readonly string[] = [],
): SettingsStore {
  const fallback = defaultSettings(languages);
  let initial = fallback;
  try {
    const raw = kv.get(KEY);
    if (raw) initial = sanitize(JSON.parse(raw) as Partial<Settings>, fallback);
  } catch {
    initial = fallback;
  }
  const store = createStore<Settings>(() => initial) as SettingsStore;
  store.update = (patch) => {
    store.setState(patch);
    try {
      kv.set(KEY, JSON.stringify(store.getState()));
    } catch {
      // Storage can be unavailable (private mode); settings still work for this session.
    }
  };
  return store;
}
